import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";
import { Select, Spin } from "antd";

import type { SelectProps } from "antd";
import type { DefaultOptionType } from 'antd/es/select';

import { RootState } from "Redux/store";
import { fetchUserTypes, fetchUserType } from "Redux/Admin/HabitManager/UserTypes/action";

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
  /** optional debouncing time for search input */
  debounceTimeout?: number;
}

/**
 * A debounced user-select component that fetches user data 
 * from the server whenever the user types in the search box.
 */
function HabitUserTypeSelect<
  ValueType extends {
    key?: string;
    label: React.ReactNode;
    value: string | number;
  } = any,
>({
  debounceTimeout = 800,
  onChange,
  ...props
}: DebounceSelectProps<ValueType>) {
  const dispatch = useDispatch();
  const { entities, ids, loading } = useSelector((state: RootState) => state.fetchUserTypes);

  // Dispatch an action on first render to load initial data
  useEffect(() => {
    dispatch(fetchUserTypes({ search: "", offset: 0, limit: 50 }, () => {}));

    if (props.value && typeof props.value === 'number') {
      dispatch(fetchUserType(props.value));
    }
  }, [dispatch, props.value]);

  const handleChange = (value: ValueType | ValueType[], option?: DefaultOptionType | DefaultOptionType[]) => {
    if (onChange) {
      onChange(value, option);
    }
  }

  // Create a debounced version of the search handler
  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      dispatch(fetchUserTypes({ search: value, offset: 0, limit: 50 }, () => {}));
    };
    return debounce(loadOptions, debounceTimeout);
  }, [debounceTimeout, dispatch]);

  const dataList = ids.map((id: string) => ({
    label: entities[id].name,
    value: entities[id].id.toString()
  })) || [];

  return (
    <Select
      // Spread any "leftover" props you might want to pass 
      // (e.g., value, onChange, mode, etc.)
      {...props}
      showSearch
      loading={loading}
      options={dataList}
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={loading ? <Spin size="small" /> : null}
      labelInValue
      onChange={handleChange}
      value={props.value || undefined}
    />
  );
}

export default HabitUserTypeSelect;
